// Earthquake advisory PDF, drawn to match "EQ ADVISORY-Eng.svg" (A4, mm).
// All coordinates, sizes and colours were measured from the reference
// artwork. The reference uses display faces Helvetica lacks, so every text
// run is sized by its measured cap height and horizontally scaled (the PDF
// "Tz" operator) so that a calibration string takes the same width as in the
// SVG; any other string then gets the same proportions.
import { preloadReportLogos } from "../../../common/reportTemplate/reportTemplate.js";

const PAGE_W = 210;
const CAP = 0.718; // Helvetica cap height / em
const PT = 0.3528; // mm per pt

const C = {
  orange: "#FF914D",
  navy: "#020187",
  blue: "#093585",
  green: "#E8F8D5",
  tan: "#F6E2B5",
  lavender: "#C2CDFF",
  pink: "#FDDEDE",
  badge: "#FF5757",
  ok: "#00BF63",
  red: "#FF5757",
  alert: "#FF2A2A",
  important: "#FF6701",
  contact: "#FF751F",
  footer: "#004AAD",
  eventInfo: "#0A3585",
  boxBorder: "#D9D9D9",
  ink: "#000000",
  white: "#FFFFFF",
};

const hex = (h) => [1, 3, 5].map((i) => parseInt(h.substring(i, i + 2), 16));
const fill = (pdf, h) => pdf.setFillColor(...hex(h));

// ---------------------------------------------------------------------------
// Text content (English from the SVG with its typos corrected; Tetum drafted
// from the wording already used in the app's earthquake advisories).
// ---------------------------------------------------------------------------
export const EQ_CONTENT = {
  en: {
    title: "TIMOR LESTE EARTHQUAKE ADVISORY",
    whatToDo: "WHAT TO DO DURING SHAKING :",
    tip: "Protect yourself first, move only a few steps to a safety zone",
    tsunami: "TSUNAMI",
    yes: "YES",
    none: "NONE",
    important: "IMPORTANT",
    footerMessage: "Aftershocks may occur. Stay in a safe place until shaking stops",
    emergency: "In emergency please call : 115 (APC); 112 (PNTL); 110 (SNAEM)",
    cards: {
      indoors: {
        heading: "INDOORS",
        bullets: [
          "DROP, COVER AND HOLD ON under a table",
          "Stay away from windows, glass and falling objects",
          "Stay inside until shaking stops and it is safe to exit",
        ],
        widths: [72.58, 84.61, 85.44],
      },
      outdoors: {
        heading: "OUTDOORS",
        bullets: [
          "Move away from building, trees, lights and wires",
          "Stay in an open area until shaking stops",
          "Avoid exits and exterior walls",
        ],
        widths: [81.59, 67.33, 49.79],
      },
      vehicle: {
        heading: "IN A VEHICLE",
        bullets: [
          "Stop as soon as safely possible and stay inside",
          "Avoid building, trees, overpasses and wires",
          "After shaking, avoid damaged roads, bridges and ramps",
        ],
        widths: [78.97, 73.41, 94.46],
      },
      trapped: {
        heading: "TRAPPED/DEBRIS",
        bullets: [
          "Do not light a match or stir up dust",
          "Cover your mouth with cloth",
          "Tap walls/pipes or use a whistle",
          "Shout only as a last resort",
        ],
        widths: [59.19, 48.69, 53.42, 44.23],
      },
    },
  },
  tet: {
    title: "AVISU RAI-NAKDOKO TIMOR LESTE",
    whatToDo: "SAIDA MAKA HALO BAINHIRA NAKDOKO :",
    tip: "Proteje an uluk, muda de'it pasu uitoan ba fatin seguru",
    tsunami: "TSUNAMI",
    yes: "SIM",
    none: "LAE",
    important: "IMPORTANTE",
    footerMessage: "Rai-nakdoko tuir mai bele akontese. Hela iha fatin seguru to'o nakdoko para",
    emergency: "In emergency please call : 115 (APC); 112 (PNTL); 110 (SNAEM)",
    cards: {
      indoors: {
        heading: "IHA UMA LARAN",
        bullets: [
          "TOBA ba rai, SUBAR iha meza ne'ebé metin no MANTEIN",
          "Hadook an husi vidru, janela no buat sira ne'ebé bele monu",
          "Hela iha laran to'o nakdoko para no seguru atu sai",
        ],
      },
      outdoors: {
        heading: "IHA LIUR",
        bullets: [
          "Hadook-an husi edifísiu, ai-hun, lampu no fiu sira",
          "Hela iha fatin luan to'o nakdoko para",
          "Evita odamatan no parede liur sira",
        ],
      },
      vehicle: {
        heading: "IHA KARETA",
        bullets: [
          "Para lalais bainhira seguru no hela iha kareta laran",
          "Evita edifísiu, ai-hun, ponte no fiu sira",
          "Bainhira nakdoko para, evita estrada no ponte ne'ebé estraga",
        ],
      },
      trapped: {
        heading: "METIN IHA RAI-RAHUN",
        bullets: [
          "Labele sunu ahi no labele hasai rai-rahun",
          "Taka ita-boot nia ibun ho lensu ka roupa",
          "Kaer ba kanu ka parede, ka uza apitu",
          "Hakilar de'it hanesan rekursu ikus",
        ],
      },
    },
  },
};

// ---------------------------------------------------------------------------
// Text helper
// ---------------------------------------------------------------------------
// opts: x, y (baseline), capH (mm), color, ref {text, w} calibration,
//       bold | medium, align, maxW (mm; shrinks horizontally to fit).
// Returns the drawn width in mm.
function drawText(pdf, text, opts) {
  const { x, y, capH, color, ref, bold = false, medium = false, align = "left", maxW, apply = true, weight = 0.12 } = opts;
  const size = capH / CAP / PT;
  pdf.setFont("helvetica", bold ? "bold" : "normal").setFontSize(size);
  let scale = 1;
  if (ref) scale = Math.min(1.12, Math.max(0.5, ref.w / pdf.getTextWidth(ref.text)));
  let w = pdf.getTextWidth(text) * scale;
  if (maxW && w > maxW) { scale *= maxW / w; w = maxW; }
  if (!apply) return w;
  const left = align === "center" ? x - w / 2 : align === "right" ? x - w : x;
  const rgb = hex(color);
  pdf.setTextColor(...rgb).setDrawColor(...rgb).setLineWidth(weight);
  pdf.internal.write(`${(scale * 100).toFixed(2)} Tz`);
  pdf.text(text, left, y, medium ? { renderingMode: "fillThenStroke" } : undefined);
  pdf.internal.write("100 Tz");
  return w;
}

// Width a string would take with the same sizing rules (no drawing).
const measure = (pdf, text, opts) => drawText(pdf, text, { ...opts, apply: false });

// ---------------------------------------------------------------------------
// Header (same artwork as every advisory; title width differs per hazard)
// ---------------------------------------------------------------------------
export const formatIssuedAt = (date = new Date()) => {
  const parts = {};
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Dili", day: "2-digit", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(date).forEach((p) => { parts[p.type] = p.value; });
  const hh = parts.hour === "24" ? "00" : parts.hour;
  return `${parts.day} ${parts.month.toUpperCase()} ${parts.year} * ${hh}:${parts.minute} TLT`;
};

function drawHeader(pdf, [apcLogo, dnmgLogo], title, issuedAt) {
  fill(pdf, C.orange);
  pdf.rect(0, 0, PAGE_W, 29.5, "F");
  if (apcLogo) { try { pdf.addImage(apcLogo, "PNG", 3.4, 0.9, 26, 26); } catch (e) { /* text still renders */ } }
  if (dnmgLogo) { try { pdf.addImage(dnmgLogo, "PNG", 179.1, 0.3, 28.7, 28.7); } catch (e) { /* text still renders */ } }

  // Title: heavy navy, condensed to the SVG's 102.3 mm.
  const tSize = 6.7 / CAP / PT;
  pdf.setFont("helvetica", "bold").setFontSize(tSize);
  const ref = "TIMOR LESTE EARTHQUAKE ADVISORY";
  let scale = Math.min(1, 102.3 / pdf.getTextWidth(ref));
  let w = pdf.getTextWidth(title) * scale;
  if (w > 140) { scale *= 140 / w; w = 140; }
  pdf.setTextColor(...hex(C.navy)).setDrawColor(...hex(C.navy)).setLineWidth(0.3);
  pdf.internal.write(`${(scale * 100).toFixed(2)} Tz`);
  pdf.text(title, 102.95 - w / 2, 10.6, { renderingMode: "fillThenStroke" });
  pdf.internal.write("100 Tz");

  drawText(pdf, "DISASTER RISK REDUCTION DECISION SUPPORT SYSTEM", {
    x: 108, y: 17.8, capH: 3.5, color: C.blue, bold: true, align: "center",
    ref: { text: "DISASTER RISK REDUCTION DECISION SUPPORT SYSTEM", w: 138.2 },
  });
  drawText(pdf, `ISSUED BY: DNMG-APC * ${issuedAt}`, {
    x: 108.6, y: 24.7, capH: 3.3, color: C.blue, bold: true, align: "center",
    ref: { text: "ISSUED BY: DNMG-APC * 25 AUGUST 2026 * 10:35 TLT", w: 112.8 },
  });
}

// ---------------------------------------------------------------------------
// Event summary card
// ---------------------------------------------------------------------------
const COMPASS = {
  N: "north", NNE: "north-northeast", NE: "northeast", ENE: "east-northeast", E: "east",
  ESE: "east-southeast", SE: "southeast", SSE: "south-southeast", S: "south", SSW: "south-southwest",
  SW: "southwest", WSW: "west-southwest", W: "west", WNW: "west-northwest", NW: "northwest", NNW: "north-northwest",
};

// "52.8 km W of Rabaul, PG" -> "An earthquake occurred 52.8 km west of Rabaul, PG".
// Titles that aren't in that "<n> km <dir> of <place>" shape are shown as given.
export function describeEarthquake(text) {
  const m = String(text || "").match(/(\d+(?:\.\d+)?)\s*km\s+([NSEW]{1,3})\s+of\s+(.+?)\s*$/i);
  if (!m || !COMPASS[m[2].toUpperCase()]) return String(text || "");
  return `An earthquake occurred ${m[1]} km ${COMPASS[m[2].toUpperCase()]} of ${m[3]}`;
}

// Event-card text: a little smaller and lighter than the SVG's 3.05 mm caps.
const EV_CAP = 2.8;
const EV_K = EV_CAP / 3.05;
const EV_WEIGHT = 0.07;

function drawEventCard(pdf, ev, t) {
  fill(pdf, C.green);
  pdf.roundedRect(4.9, 35.0, 200, 38.2, 4.5, 4.5, "F");

  // magnitude badge
  fill(pdf, C.badge);
  pdf.circle(22.05, 55.57, 11.03, "F");
  drawText(pdf, ev.magnitude, {
    x: 21.9, y: 58.8, capH: 6.1, color: C.white, bold: true, align: "center",
    ref: { text: "4.9", w: 12 }, maxW: 17,
  });

  const maxW = 162.4 - 3 - 38.1;
  const o = { capH: EV_CAP, medium: true, weight: EV_WEIGHT, maxW: maxW + 2 };
  // same proportions as the SVG's lines, scaled with the smaller type size
  const infoRef = { text: "Depth: 10 km  *  10:18:46 TLT  *  Lat: 34.462 Lon 70.40.463", w: 106.1 * EV_K };
  drawText(pdf, describeEarthquake(ev.headline), {
    ...o, x: 37.8, y: 46.0, color: C.ink, maxW,
    ref: { text: "4Km NNE of Jalalabd, Afganistan", w: 61.1 * EV_K },
  });
  drawText(pdf, ev.detail, { ...o, x: 37.8, y: 52.4, color: C.eventInfo, ref: infoRef });
  drawText(pdf, ev.coords, { ...o, x: 37.8, y: 58.8, color: C.eventInfo, ref: infoRef });
  drawText(pdf, ev.magLine, {
    ...o, x: 37.8, y: 65.2, color: C.eventInfo,
    ref: { text: "Magnitude: 4.9 SR   Source: RIMES", w: 63.7 * EV_K },
  });

  // tsunami readout
  fill(pdf, C.white);
  pdf.setDrawColor(...hex(C.boxBorder)).setLineWidth(0.8);
  pdf.roundedRect(162.8, 44.85, 34.1, 20.3, 3, 3, "FD");
  drawText(pdf, t.tsunami, {
    x: 179.1, y: 51.85, capH: 3.6, color: C.ok, bold: true, align: "center",
    ref: { text: "TSUNAMI", w: 21 }, maxW: 31,
  });
  drawText(pdf, ev.tsunami, {
    x: 179.3, y: 60.05, capH: 3.6, color: C.red, bold: true, align: "center",
    ref: { text: "NONE", w: 12.8 }, maxW: 31,
  });
  pdf.setLineWidth(0.2).setDrawColor(0, 0, 0);
}

// ---------------------------------------------------------------------------
// Map (centre-cropped to the SVG's 117.3 x 67.0 mm box)
// ---------------------------------------------------------------------------
const MAP = { x: 50.8, y: 78.05, w: 117.3, h: 67.0 };

function cropToBox(img, boxW, boxH) {
  const targetAspect = boxW / boxH;
  const srcAspect = img.width / img.height;
  let sw = img.width;
  let sh = img.height;
  if (srcAspect > targetAspect) sw = img.height * targetAspect;
  else sh = img.width / targetAspect;
  const sx = (img.width - sw) / 2;
  const sy = (img.height - sh) / 2;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(sw);
  canvas.height = Math.round(sh);
  canvas.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/png");
}

// ---------------------------------------------------------------------------
// "What to do" cards
// ---------------------------------------------------------------------------
const CARDS = [
  { key: "indoors", bg: C.green, rect: [3.6, 157.0, 101.1, 49.2], icon: [48.61, 163.91], headX: 49.15, headY: 176.6, headRef: { text: "INDOORS", w: 14.26 }, dotX: 5.5, textX: 10.05, firstY: 188.2 },
  { key: "outdoors", bg: C.tan, rect: [106.1, 157.0, 101.2, 49.2], icon: [155.66, 163.45], headX: 156.75, headY: 174.8, headRef: { text: "INDOORS", w: 14.31 }, dotX: 109.6, textX: 114.3, firstY: 188.85 },
  { key: "vehicle", bg: C.lavender, rect: [3.6, 207.9, 101.1, 49.2], icon: [48.61, 215.27], headX: 49.7, headY: 226.4, headRef: { text: "IN A VEHICLE", w: 20.12 }, dotX: 4.9, textX: 9.45, firstY: 233.65 },
  { key: "trapped", bg: C.pink, rect: [105.5, 207.9, 101.2, 49.2], icon: [157.65, 215.27], headX: 157.8, headY: 226.2, headRef: { text: "TRAPPED/DEBRIS", w: 27.08 }, dotX: 108.2, textX: 112.95, firstY: 233.65 },
];
const ICON_SIZE = 13.6;
// Calibrated on two SVG lines (85.5 mm + 94.5 mm) so no single card dominates.
const BULLET_REF = {
  text: "Stay inside until shaking stops and it is safe to exitAfter shaking, avoid damaged roads, bridges and ramps",
  w: 180,
};

function wrapBullet(pdf, text, opts, maxW) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let cur = "";
  words.forEach((word) => {
    const next = cur ? `${cur} ${word}` : word;
    // small overflows are squeezed horizontally by drawText instead of wrapping
    if (cur && measure(pdf, next, { ...opts, maxW: undefined }) > maxW * 1.08) { lines.push(cur); cur = word; } else { cur = next; }
  });
  if (cur) lines.push(cur);
  return lines;
}

function drawCard(pdf, card, content, icons, useMeasured) {
  const [x, y, w, h] = card.rect;
  fill(pdf, card.bg);
  pdf.roundedRect(x, y, w, h, 3.8, 3.8, "F");

  const icon = icons[card.key];
  if (icon) {
    try {
      pdf.addImage(icon, "PNG", card.icon[0] - ICON_SIZE / 2, card.icon[1] - ICON_SIZE / 2, ICON_SIZE, ICON_SIZE);
    } catch (e) { /* card still renders without its icon */ }
  }

  drawText(pdf, content.heading, {
    x: card.headX, y: card.headY, capH: 2.4, color: C.ink, medium: true, align: "center",
    ref: useMeasured ? card.headRef : { text: "INDOORS", w: 14.3 }, maxW: w - 8,
  });

  // bullets: shrink a little if a (translated) list would overflow the card
  const maxTextW = x + w - card.textX - 0.8;
  const bottom = y + h - 2.2;
  let factor = 1;
  let lines;
  let pitch;
  for (let i = 0; i < 8; i++) {
    const capH = 2.8 * factor;
    pitch = 4.7 * factor;
    const o = { capH, medium: true, color: C.ink, ref: BULLET_REF };
    lines = content.bullets.map((b, bi) => (
      useMeasured && content.widths ? [{ text: b, ref: { text: b, w: content.widths[bi] } }]
        : wrapBullet(pdf, b, o, maxTextW).map((text) => ({ text }))
    ));
    const rows = lines.reduce((n, l) => n + l.length, 0);
    if (card.firstY + (rows - 1) * pitch <= bottom) break;
    factor *= 0.93;
  }
  const capH = 2.8 * factor;
  let by = card.firstY;
  lines.forEach((bulletLines) => {
    fill(pdf, C.ink);
    pdf.circle(card.dotX + 0.55, by - capH / 2, 0.55 * factor, "F");
    bulletLines.forEach((ln) => {
      drawText(pdf, ln.text, { x: card.textX, y: by, capH, color: C.ink, medium: true, ref: ln.ref || BULLET_REF, maxW: maxTextW });
      by += pitch;
    });
  });
}

// ---------------------------------------------------------------------------
// Footer bar
// ---------------------------------------------------------------------------
function drawFooter(pdf, t) {
  fill(pdf, C.footer);
  pdf.roundedRect(3.5, 266.7, 203.2, 27.0, 5, 5, "F");

  fill(pdf, C.alert);
  pdf.circle(24.35, 278.0, 8.65, "F");
  fill(pdf, C.white);
  pdf.roundedRect(24.35 - 1.0, 278.0 - 6.2, 2.0, 7.8, 1, 1, "F");
  pdf.circle(24.35, 278.0 + 4.6, 1.3, "F");

  drawText(pdf, t.important, { x: 39.5, y: 272.4, capH: 3.3, color: C.important, bold: true, ref: { text: "IMPORTANT", w: 26.4 }, maxW: 60 });
  drawText(pdf, t.footerMessage, {
    x: 38.0, y: 279.3, capH: 4.0, color: C.white, bold: true, maxW: 166,
    ref: { text: "Afterschocks may occur. Stay in a  safe place until shaking stops", w: 162.2 },
  });
  // emergency + contact lines: a little smaller than the SVG's 3.4 mm caps
  const kc = 3.0 / 3.4;
  drawText(pdf, t.emergency, {
    x: 38.7, y: 286.0, capH: 3.0, color: C.red, bold: true, maxW: 166,
    ref: { text: "In emergency please call : 115 (APC); 112 (PNTL); 110 (SNAEM)", w: 134.6 * kc },
  });
  drawText(pdf, "drr.dnmg.gov.tl  *  geofisica@dnmg.gov.tl  *  +670 7709 4893", {
    x: 38.8, y: 291.2, capH: 3.0, color: C.contact, bold: true, maxW: 166,
    ref: { text: "drr.dnmg.gov.tl  *  geofisica@dnmg.gov.tl  *  +670 7709 4893", w: 130.2 * kc },
  });
}

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------
let iconCache = null;
const loadAsDataUrl = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (e) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

async function loadIcons() {
  if (!iconCache) {
    iconCache = Promise.all(
      CARDS.map((c) => loadAsDataUrl(`/report-assets/eq/${c.key}.png`))
    ).then((urls) => Object.fromEntries(CARDS.map((c, i) => [c.key, urls[i]])));
  }
  return iconCache;
}

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/**
 * Builds the one-page earthquake advisory into `pdf` (a fresh jsPDF A4).
 * event: { headline, detail, magLine, magnitude, tsunami: "NONE"|"YES" key }
 */
export async function drawEarthquakeAdvisory(pdf, { language: lang, mapDataUrl, event }) {
  let language = lang;
  language = EQ_CONTENT[language] ? language : "en";
  const t = EQ_CONTENT[language];
  const [logos, icons] = await Promise.all([preloadReportLogos(), loadIcons()]);

  drawHeader(pdf, logos, t.title, formatIssuedAt());
  drawEventCard(pdf, { ...event, tsunami: event.tsunami === "YES" ? t.yes : t.none }, t);

  if (mapDataUrl) {
    try {
      const img = await loadImage(mapDataUrl);
      pdf.addImage(cropToBox(img, MAP.w, MAP.h), "PNG", MAP.x, MAP.y, MAP.w, MAP.h);
    } catch (e) { /* map unavailable — leave the area blank */ }
  }

  // "WHAT TO DO DURING SHAKING :" (red) + tip (green), centred as one line
  // a little smaller than the SVG's 3.5 mm caps; widths scale with it
  const WTD_CAP = 3.1;
  const wk = WTD_CAP / 3.5;
  const redRef = { text: "WHAT TO DO DURING SHAKING :", w: 67.2 * wk };
  const tipRef = { text: "Protect yourself first, move only a few stepts to safezty zone", w: 123.8 * wk };
  const redOpts = { capH: WTD_CAP, bold: true, ref: redRef, color: C.red };
  const tipOpts = { capH: WTD_CAP, bold: true, ref: tipRef, color: C.ok };
  const gap = 1.2;
  let rw = measure(pdf, t.whatToDo, redOpts);
  let tw = measure(pdf, t.tip, tipOpts);
  const k = Math.min(1, 192.2 / (rw + gap + tw));
  rw *= k; tw *= k;
  const startX = 103.3 - (rw + gap + tw) / 2;
  drawText(pdf, t.whatToDo, { ...redOpts, x: startX, y: 153.95, maxW: rw });
  drawText(pdf, t.tip, { ...tipOpts, x: startX + rw + gap, y: 153.95, maxW: tw });

  CARDS.forEach((card) => drawCard(pdf, card, t.cards[card.key], icons, language === "en"));
  drawFooter(pdf, t);
}
